import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Chip, Grid, IconButton, Table, TableHead, TableRow, TableCell, TableBody,
  Typography, FormControlLabel, Switch, Alert, CircularProgress, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import { InvTextField, InvSelect } from '../../components/InvField';
import BatchChip from '../../components/BatchChip';
import {
  createReturn,
  confirmReturn,
  getDispenses,
  getDispenseItems,
  getPatientBatchExposure,
} from '../../api/dispensing';
import { getStores, getStockBalances } from '../../api/stockLedger';
import { toInventoryList } from '../../api/inventory-request';
import useItemNames from '../../hooks/useItemNames';
import { generateTxnNumber } from '../../utils';
import { useEmrContext } from '../../hooks/useEmrContext';
import { getStoreActorId } from '../../hooks/useStoreScope';
import { IPD_COLORS } from '../../theme';
import { fadeInSx, lineTableSx, summaryHighlightSx } from './pharmacyStyles';
import { EmrContextPanel } from './PharmacyUi';
import { isControlledLineNonReturnable } from '../../utils/controlledDrug';

const LINE_DEFAULT = {
  item_id: '',
  batch_number: '',
  return_quantity: '',
  unit_rate: '',
  reason: '',
};

function normalizeExposureRows(payload) {
  return toInventoryList(payload);
}

export default function PharmacyReturn() {
  const history = useHistory();
  const { url } = useRouteMatch();
  const base = url.replace('/pharmacy/return/new', '');
  const emr = useEmrContext();
  const {
    items: catalogItems, itemOptionLabel: labelItem, resolveItem, catalog,
    getIssueUomCode, getIssueUomId,
  } = useItemNames();

  const [saving, setSaving] = useState(false);
  const [stores, setStores] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [lines, setLines] = useState([{ ...LINE_DEFAULT }]);
  const [triggerBillingReversal, setTriggerBillingReversal] = useState(false);
  const [patientExposure, setPatientExposure] = useState([]);
  const [patientDispenses, setPatientDispenses] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [loadingDispenseItems, setLoadingDispenseItems] = useState(false);

  const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      transaction_number: generateTxnNumber('PRET'),
      to_store_id: '',
      transaction_date: dayjs().format('YYYY-MM-DD'),
      external_patient_id: emr.external_patient_id || '',
      source_dispense_id: '',
      remarks: '',
    },
  });

  const toStoreId = watch('to_store_id');
  const patientId = watch('external_patient_id');
  const sourceDispenseId = watch('source_dispense_id');

  const medicineItems = useMemo(
    () => catalogItems.filter(i => i.item_type === 'MEDICINE'),
    [catalogItems],
  );
  const validLineCount = lines.filter(l => l.item_id && Number(l.return_quantity) > 0).length;
  const catalogLoading = !catalog;

  useEffect(() => {
    getStores({ is_pharmacy_store: true })
      .then(d => setStores(Array.isArray(d) ? d : (d?.items || [])))
      .catch(() => toast.error('Failed to load pharmacy stores'));
  }, []);

  useEffect(() => {
    if (!toStoreId) {
      setStockItems([]);
      return;
    }
    getStockBalances({ store_id: toStoreId })
      .then(d => setStockItems(Array.isArray(d) ? d : (d?.items || [])))
      .catch(() => {});
  }, [toStoreId]);

  const loadPatientHistory = useCallback(async () => {
    const pid = (patientId || '').trim();
    if (!pid) {
      setPatientExposure([]);
      setPatientDispenses([]);
      setHistoryError(null);
      return;
    }

    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const admissionId = emr.external_admission_id || undefined;
      const [exposure, dispenses] = await Promise.all([
        getPatientBatchExposure(pid, admissionId),
        getDispenses({
          external_patient_id: pid,
          status: 'DISPENSED',
        }),
      ]);
      setPatientExposure(normalizeExposureRows(exposure));
      setPatientDispenses(toInventoryList(dispenses));
    } catch (e) {
      setPatientExposure([]);
      setPatientDispenses([]);
      setHistoryError(e.message || 'Could not load patient dispense history');
    } finally {
      setHistoryLoading(false);
    }
  }, [patientId, emr.external_admission_id]);

  useEffect(() => {
    const t = setTimeout(() => { loadPatientHistory(); }, 400);
    return () => clearTimeout(t);
  }, [loadPatientHistory]);

  useEffect(() => {
    setValue('source_dispense_id', '');
  }, [patientId, setValue]);

  function stockForItem(itemId) {
    return stockItems.find(s => String(s.item_id || s.id) === String(itemId));
  }

  function updateLine(i, key, val) {
    setLines(l => l.map((row, idx) => {
      if (idx !== i) return row;
      const updated = { ...row, [key]: val };
      if (key === 'item_id') {
        const stock = stockForItem(val);
        if (stock) {
          updated.batch_number = stock.batch_number || updated.batch_number;
          updated.unit_rate = stock.unit_rate ?? stock.mrp ?? updated.unit_rate;
        }
      }
      return updated;
    }));
  }

  function addLineFromExposure(row) {
    const itemId = row.item_id || row.id;
    if (!itemId) return;
    setLines(l => [...l, {
      item_id: itemId,
      batch_number: row.batch_number || '',
      return_quantity: row.quantity != null ? String(row.quantity) : '',
      unit_rate: row.unit_rate != null ? String(row.unit_rate) : '',
      reason: 'Patient return',
    }]);
  }

  async function loadLinesFromDispense(dispenseId) {
    if (!dispenseId) return;
    setLoadingDispenseItems(true);
    try {
      const items = await getDispenseItems(dispenseId);
      const list = toInventoryList(items);
      if (!list.length) {
        toast.info('No line items found on the selected dispense');
        return;
      }
      setLines(list.map(i => ({
        item_id: i.item_id || i.id || '',
        batch_number: i.batch_number || '',
        return_quantity: i.quantity != null ? String(i.quantity) : '',
        unit_rate: i.unit_rate != null ? String(i.unit_rate) : '',
        reason: '',
      })));
      toast.success(`Loaded ${list.length} item${list.length > 1 ? 's' : ''} from dispense`);
    } catch (e) {
      toast.error(e.message || 'Failed to load dispense items');
    } finally {
      setLoadingDispenseItems(false);
    }
  }

  useEffect(() => {
    if (sourceDispenseId) {
      loadLinesFromDispense(sourceDispenseId);
    }
    // Only react to dispense selection changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceDispenseId]);

  async function onSubmit(data) {
    const validLines = lines.filter(l => l.item_id && Number(l.return_quantity) > 0);
    const blocked = validLines.filter((l) => isControlledLineNonReturnable(l.item_id, catalog));
    if (blocked.length) {
      toast.error('Controlled / narcotic medicines cannot be returned');
      return;
    }
    if (!validLines.length) {
      toast.error('Add at least one medicine with a return quantity');
      return;
    }
    if (!(data.external_patient_id || '').trim()) {
      toast.error('Patient ID is required for a patient return');
      return;
    }

    setSaving(true);
    try {
      const ret = await createReturn({
        transaction_number: data.transaction_number,
        to_store_id: data.to_store_id,
        transaction_date: data.transaction_date,
        remarks: data.remarks || undefined,
        external_patient_id: data.external_patient_id.trim(),
        external_admission_id: emr.external_admission_id || undefined,
        return_type: 'PATIENT_RETURN',
        source_dispense_id: data.source_dispense_id || undefined,
        items: validLines.map(l => ({
          item_id: l.item_id,
          batch_number: l.batch_number || undefined,
          return_quantity: Number(l.return_quantity),
          // The unit the quantity above is counted in. The service falls back
          // to the source dispense line's UOM, but a row typed in by hand has
          // no source line — without this it would credit strips as tablets.
          uom_id: l.uom_id || getIssueUomId(l.item_id) || undefined,
          unit_rate: l.unit_rate ? Number(l.unit_rate) : undefined,
          reason: l.reason || undefined,
        })),
      });

      const retId = ret.transaction_id || ret.id;
      if (!retId) {
        throw new Error('Return created but no transaction ID was returned');
      }

      const actor = getStoreActorId() || 'pharmacist';
      await confirmReturn(retId, {
        confirmed_by: actor,
        trigger_billing_reversal: triggerBillingReversal,
      });

      toast.success('Patient return processed successfully');
      history.push(`${base}/pharmacy`);
    } catch (e) {
      toast.error(e.message || 'Failed to process patient return');
    } finally {
      setSaving(false);
    }
  }

  const storeOptions = stores.map(s => ({ value: s.store_id || s.id, label: s.store_name }));
  const itemOptions = medicineItems.map(i => ({ value: i.item_id || i.id, label: labelItem(i) }));
  const dispenseOptions = [
    { value: '', label: 'Select source dispense (optional)' },
    ...patientDispenses.map(d => ({
      value: d.transaction_id || d.id,
      label: `${d.transaction_number || d.transaction_id || d.id} · ${formatDispenseDate(d.transaction_date)}`,
    })),
  ];

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={fadeInSx}>
      <PageHeader
        eyebrow="Pharmacy Management"
        title="Patient Medicine Return"
        description="Return medicines dispensed to a patient and optionally reverse billing."
        actions={
          <>
            <InvButton variant="outlined" tone="teal" onClick={() => history.goBack()}>Cancel</InvButton>
            <InvButton type="submit" loading={saving} disabled={validLineCount === 0}>
              Process Return
            </InvButton>
          </>
        }
      />

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={8}>
          <SectionCard title="Return Details" subtitle="Patient, destination store, and optional source dispense">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Controller name="transaction_number" control={control} rules={{ required: 'Required' }}
                  render={({ field }) => <InvTextField {...field} label="Return *" error={errors.transaction_number} />} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller name="to_store_id" control={control} rules={{ required: 'Required' }}
                  render={({ field }) => (
                    <InvSelect label="Return to Store *" value={field.value} onChange={field.onChange}
                      options={storeOptions} error={errors.to_store_id} />
                  )} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller name="transaction_date" control={control}
                  render={({ field }) => <InvTextField {...field} label="Date" type="date" InputLabelProps={{ shrink: true }} />} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller name="external_patient_id" control={control} rules={{ required: 'Patient ID is required' }}
                  render={({ field }) => (
                    <InvTextField {...field} label="Patient ID *" error={errors.external_patient_id}
                      helperText={emr.external_patient_id ? 'Pre-filled from patient chart' : undefined} />
                  )} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller name="source_dispense_id" control={control}
                  render={({ field }) => (
                    <InvSelect
                      label="Source Dispense"
                      value={field.value}
                      onChange={field.onChange}
                      options={dispenseOptions}
                      disabled={!patientId || historyLoading || patientDispenses.length === 0}
                    />
                  )} />
              </Grid>
              <Grid item xs={12}>
                <Controller name="remarks" control={control}
                  render={({ field }) => <InvTextField {...field} label="Remarks" multiline rows={2} />} />
              </Grid>
            </Grid>
          </SectionCard>

          {(patientId || '').trim() && (
            <SectionCard
              title="Patient Dispense History"
              subtitle="Medicines previously dispensed to this patient"
              sx={{ mt: 2.5 }}
              actions={
                <InvButton size="small" variant="outlined" tone="teal" startIcon={<RefreshIcon />}
                  onClick={loadPatientHistory} loading={historyLoading}>
                  Refresh
                </InvButton>
              }
            >
              {historyLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
                  <CircularProgress size={22} sx={{ color: IPD_COLORS.primary }} />
                  <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
                    Loading patient batch exposure…
                  </Typography>
                </Box>
              )}
              {historyError && !historyLoading && (
                <Alert severity="warning" sx={{ borderRadius: '10px', mb: 2 }}>{historyError}</Alert>
              )}
              {!historyLoading && !historyError && patientExposure.length === 0 && (
                <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, py: 1 }}>
                  No dispensed medicines found for this patient. You can still add items manually below.
                </Typography>
              )}
              {!historyLoading && patientExposure.length > 0 && (
                <Box sx={{ ...lineTableSx, overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Medicine</TableCell>
                        <TableCell>Batch</TableCell>
                        <TableCell align="right">Dispensed Qty</TableCell>
                        <TableCell sx={{ width: 90 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {patientExposure.map((row, i) => (
                        <TableRow key={`${row.item_id}-${row.batch_number}-${i}`}>
                          <TableCell sx={{ fontWeight: 500 }}>{resolveItem(row)}</TableCell>
                          <TableCell>
                            <BatchChip batchNumber={row.batch_number} expiryDate={row.expiry_date} />
                          </TableCell>
                          <TableCell align="right">{row.quantity ?? '—'}</TableCell>
                          <TableCell>
                            <Tooltip title="Add to return">
                              <IconButton size="small" onClick={() => addLineFromExposure(row)}
                                sx={{ color: IPD_COLORS.primary }}>
                                <PlaylistAddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
              {loadingDispenseItems && (
                <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, mt: 1 }}>
                  Loading items from selected dispense…
                </Typography>
              )}
            </SectionCard>
          )}

          <SectionCard
            title="Medicines Being Returned"
            subtitle={
              catalogLoading
                ? 'Loading medicine catalog…'
                : `${medicineItems.length} medicines in catalog${toStoreId ? ` · stock loaded for selected store` : ''}`
            }
            sx={{ mt: 2.5 }}
            actions={
              <InvButton size="small" startIcon={<AddIcon />} onClick={() => setLines(l => [...l, { ...LINE_DEFAULT }])}>
                Add Row
              </InvButton>
            }
          >
            <Box sx={{ ...lineTableSx, overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 220 }}>Medicine</TableCell>
                    <TableCell sx={{ width: 130 }}>Batch</TableCell>
                    <TableCell sx={{ width: 90 }}>Qty</TableCell>
                    <TableCell sx={{ width: 100 }}>Unit Rate</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell sx={{ width: 50 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((line, i) => {
                    const nonReturnable = isControlledLineNonReturnable(line.item_id, catalog);
                    return (
                    <TableRow key={i} sx={{ opacity: nonReturnable ? 0.6 : 1 }}>
                      <TableCell>
                        <InvSelect
                          label=""
                          value={line.item_id}
                          onChange={e => updateLine(i, 'item_id', e.target.value)}
                          options={itemOptions}
                          disabled={catalogLoading || nonReturnable}
                        />
                        {nonReturnable && (
                          <Typography sx={{ fontSize: 11, color: IPD_COLORS.warning.text, mt: 0.5 }}>
                            Non-returnable (controlled drug)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <InvTextField value={line.batch_number} label=""
                          onChange={e => updateLine(i, 'batch_number', e.target.value)}
                          disabled={nonReturnable} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                          <InvTextField value={line.return_quantity} label="" type="number"
                            inputProps={{ min: 0.01, step: 0.01 }}
                            onChange={e => updateLine(i, 'return_quantity', e.target.value)}
                            disabled={nonReturnable} />
                          {/* Same unit the medicine was dispensed in. Item master's;
                              not selectable here. */}
                          {line.item_id && getIssueUomCode(line.item_id) && (
                            <Chip
                              label={getIssueUomCode(line.item_id)}
                              size="small"
                              disabled
                              sx={{ height: 22, fontSize: 10.5, fontWeight: 700 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <InvTextField value={line.unit_rate} label="" type="number"
                          inputProps={{ min: 0 }}
                          onChange={e => updateLine(i, 'unit_rate', e.target.value)}
                          disabled={nonReturnable} />
                      </TableCell>
                      <TableCell>
                        <InvTextField value={line.reason} label=""
                          onChange={e => updateLine(i, 'reason', e.target.value)}
                          disabled={nonReturnable} />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" disabled={lines.length <= 1}
                          onClick={() => setLines(l => l.filter((_, idx) => idx !== i))}
                          sx={{ color: IPD_COLORS.error.text, transition: 'transform 0.15s ease', '&:hover': { transform: 'scale(1.1)' } }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
            {validLineCount > 0 && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssignmentReturnIcon sx={{ fontSize: 18, color: IPD_COLORS.warning.text }} />
                <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
                  {validLineCount} item{validLineCount > 1 ? 's' : ''} ready to return
                </Typography>
              </Box>
            )}
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <EmrContextPanel
            patientId={patientId || emr.external_patient_id}
            admissionId={emr.external_admission_id}
            title="Patient Context"
          />

          <Box sx={{ ...summaryHighlightSx, mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AssignmentReturnIcon sx={{ fontSize: 20, color: IPD_COLORS.primary }} />
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: IPD_COLORS.textPrimary }}>
                Billing Options
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={triggerBillingReversal}
                  onChange={e => setTriggerBillingReversal(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: IPD_COLORS.primary },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: IPD_COLORS.primary },
                  }}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: 14, color: IPD_COLORS.textPrimary, fontWeight: 500 }}>
                    Trigger billing reversal
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, mt: 0.25 }}>
                    Automatically reverse the original billing charge for returned items.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', ml: 0, gap: 1 }}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

function formatDispenseDate(value) {
  if (!value) return '—';
  try {
    return dayjs(value).format('DD MMM YYYY');
  } catch {
    return String(value);
  }
}
